import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  FileText, 
  Video, 
  Music, 
  Image, 
  Package, 
  File,
  ShoppingCart,
  Clock,
  Star
} from "lucide-react";
import { DigitalContent, formatPrice, CONTENT_TYPE_LABELS } from "@/types/digitalContent";
import { getPublicDigitalContents, getDigitalContentForPurchase } from "@/services/digitalContent/publicDigitalContentService";
import { PurchaseConfirmationModal } from "@/components/digitalContent/PurchaseConfirmationModal";
import { useRole } from "@/hooks/useRole";
import { useNavigate } from "react-router-dom";

interface MentorDigitalContentsSectionProps {
  mentorId: string;
}

const getContentTypeIcon = (contentType: string) => {
  const icons = {
    pdf: <FileText className="h-4 w-4" />,
    video: <Video className="h-4 w-4" />,
    audio: <Music className="h-4 w-4" />,
    image: <Image className="h-4 w-4" />,
    zip: <Package className="h-4 w-4" />,
    document: <File className="h-4 w-4" />
  };
  return icons[contentType as keyof typeof icons] || <File className="h-4 w-4" />;
};

const getContentTypeColor = (contentType: string): string => {
  const colors = {
    pdf: 'bg-red-500/20 text-red-400 border-red-500/30',
    video: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    audio: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    image: 'bg-green-500/20 text-green-400 border-green-500/30',
    zip: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    document: 'bg-gray-500/20 text-gray-400 border-gray-500/30'
  };
  return colors[contentType as keyof typeof colors] || colors.document;
};

export const MentorDigitalContentsSection: React.FC<MentorDigitalContentsSectionProps> = ({
  mentorId
}) => {
  const [contents, setContents] = useState<DigitalContent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedContent, setSelectedContent] = useState<DigitalContent | null>(null);
  const [isPurchaseModalOpen, setIsPurchaseModalOpen] = useState(false);
  const [isLoadingPurchase, setIsLoadingPurchase] = useState(false);
  const { authUser } = useRole();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchContents = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getPublicDigitalContents(mentorId);
        setContents(data);
      } catch (err) {
        console.error("Erro ao carregar conteúdos:", err);
        setError("Erro ao carregar conteúdos digitais");
      } finally {
        setLoading(false);
      }
    };

    if (mentorId) {
      fetchContents();
    }
  }, [mentorId]);

  const handlePurchase = async (contentId: string) => {
    if (!authUser) {
      // Redirecionar para login
      window.location.href = "/login";
      return;
    }
    
    try {
      setIsLoadingPurchase(true);
      const contentData = await getDigitalContentForPurchase(contentId);
      
      if (contentData) {
        setSelectedContent(contentData);
        setIsPurchaseModalOpen(true);
      }
    } catch (error) {
      console.error("Erro ao carregar dados do conteúdo:", error);
    } finally {
      setIsLoadingPurchase(false);
    }
  };

  const handlePurchaseSuccess = () => {
    // Redirecionar para página de conteúdos comprados
    navigate('/painel-jogador/meus-conteudos');
  };

  const closePurchaseModal = () => {
    setIsPurchaseModalOpen(false);
    setSelectedContent(null);
  };

  // Proteger o estado do modal contra mudanças de visibilidade da página
  useEffect(() => {
    const handleVisibilityChange = () => {
      // Não fazer nada quando a visibilidade muda - manter o modal aberto
      return;
    };

    const handleFocus = () => {
      // Não fazer nada quando a janela ganha foco - manter o modal aberto
      return;
    };

    const handleBlur = () => {
      // Não fazer nada quando a janela perde foco - manter o modal aberto
      return;
    };

    if (isPurchaseModalOpen) {
      document.addEventListener('visibilitychange', handleVisibilityChange);
      window.addEventListener('focus', handleFocus);
      window.addEventListener('blur', handleBlur);

      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        window.removeEventListener('focus', handleFocus);
        window.removeEventListener('blur', handleBlur);
      };
    }
  }, [isPurchaseModalOpen]);

  if (loading) {
    return (
      <div className="bg-[#131922] border border-[#222A35] rounded-lg p-6">
        <h2 className="text-xl font-bold mb-4">Conteúdos Disponíveis</h2>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00B6F0]"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-[#131922] border border-[#222A35] rounded-lg p-6">
        <h2 className="text-xl font-bold mb-4">Conteúdos Disponíveis</h2>
        <div className="text-center py-8">
          <p className="text-red-400">{error}</p>
        </div>
      </div>
    );
  }

  if (contents.length === 0) {
    return null; // Não exibir seção se não há conteúdos
  }

  return (
    <>
      <div className="bg-[#131922] border border-[#222A35] rounded-lg p-6 mb-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold">Conteúdos Disponíveis</h2>
          <Badge variant="outline" className="text-[#00B6F0] border-[#00B6F0]">
            {contents.length} {contents.length === 1 ? 'conteúdo' : 'conteúdos'}
          </Badge>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {contents.map((content) => (
            <div
              key={content.id}
              className="bg-[#1A2233] border border-[#2D3748] rounded-lg overflow-hidden hover:border-[#00B6F0]/50 transition-colors"
            >
              {/* Imagem de Capa */}
              {content.cover_image_url && (
                <div className="relative h-32 overflow-hidden">
                  <img
                    src={content.cover_image_url}
                    alt={content.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1A2233]/80 to-transparent" />
                </div>
              )}

              <div className="p-4">
                {/* Tipo e Preço */}
                <div className="flex items-center justify-between mb-2">
                  <Badge 
                    variant="outline" 
                    className={`text-xs ${getContentTypeColor(content.content_type)}`}
                  >
                    {getContentTypeIcon(content.content_type)}
                    <span className="ml-1">{CONTENT_TYPE_LABELS[content.content_type]}</span>
                  </Badge>
                  
                  <div className="text-lg font-bold text-[#00B6F0]">
                    {formatPrice(content.price_cents)}
                  </div>
                </div>

                {/* Título */}
                <h3 className="font-semibold text-white mb-2 line-clamp-2 leading-tight">
                  {content.title}
                </h3>

                {/* Descrição */}
                <p className="text-sm text-gray-400 mb-3 line-clamp-2">
                  {content.description}
                </p>

                {/* Informações adicionais */}
                <div className="flex items-center gap-3 text-xs text-gray-500 mb-4">
                  {content.estimated_duration_minutes && (
                    <div className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      <span>{content.estimated_duration_minutes}min</span>
                    </div>
                  )}
                  
                  {content.download_count > 0 && (
                    <div className="flex items-center gap-1">
                      <Star className="h-3 w-3" />
                      <span>{content.download_count} downloads</span>
                    </div>
                  )}
                </div>

                {/* Tags */}
                {content.tags && content.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-4">
                    {content.tags.slice(0, 3).map((tag, index) => (
                      <Badge key={index} variant="outline" className="text-xs text-gray-400 border-gray-600">
                        {tag}
                      </Badge>
                    ))}
                    {content.tags.length > 3 && (
                      <Badge variant="outline" className="text-xs text-gray-400 border-gray-600">
                        +{content.tags.length - 3}
                      </Badge>
                    )}
                  </div>
                )}

                {/* Botão de Compra */}
                <Button
                  onClick={() => handlePurchase(content.id)}
                  className="w-full bg-[#00B6F0] hover:bg-[#0099CC] text-black font-medium"
                  disabled={!authUser || isLoadingPurchase}
                >
                  {isLoadingPurchase ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black mr-2" />
                      Carregando...
                    </>
                  ) : (
                    <>
                      <ShoppingCart className="h-4 w-4 mr-2" />
                      {content.price_cents === 0 ? 'Obter Gratuito' : 'Comprar Conteúdo'}
                    </>
                  )}
                </Button>

                {!authUser && (
                  <p className="text-xs text-gray-500 text-center mt-2">
                    Faça login para adquirir este conteúdo
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Modal de Confirmação de Compra */}
      <PurchaseConfirmationModal
        isOpen={isPurchaseModalOpen}
        onClose={closePurchaseModal}
        content={selectedContent}
        onPurchaseSuccess={handlePurchaseSuccess}
      />
    </>
  );
}; 